/**
 * What the page tells the assistant about itself.
 *
 * The overlay sits on every page, so it has to be told where it is. A page
 * registers two things:
 *
 *  - **context** — what the assistant should know before answering, appended to
 *    the *first* question asked on that page. Only the first: after that the
 *    conversation carries its own history, and repeating the page description
 *    with every turn would push it around and waste the window.
 *  - **helpText** — the opening line, "Ich helfe dir bei …". A page that says
 *    nothing gets a default; the assistant is never silent about what it is for.
 *
 * Registration is an effect, so leaving a page removes its context again. Two
 * pages mounted at once would otherwise fight over the same slot, and the loser
 * would answer for the wrong screen.
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";

export interface OverlayBotPageContext {
  /** Appended to the first question asked on this page. */
  context?: string;
  /** Opening line of the assistant, e.g. "Ich helfe dir bei der Risikobewertung." */
  helpText?: string;
}

interface OverlayBotContextValue extends OverlayBotPageContext {
  register: (id: string, value: OverlayBotPageContext) => void;
  unregister: (id: string) => void;
}

const OverlayBotContext = createContext<OverlayBotContextValue | null>(null);

let sequence = 0;
const nextId = () => `page-${(sequence += 1)}`;

export function OverlayBotProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<Record<string, OverlayBotPageContext>>({});

  const value = useMemo<OverlayBotContextValue>(() => {
    // First registration wins. Nesting is rare, and when it happens the inner
    // component is the more specific one. React runs child effects before parent
    // effects, so the inner component registers first.
    const registered = Object.values(entries);
    const latest = registered[0] ?? {};

    return {
      context: latest.context,
      helpText: latest.helpText,
      register: (id, entry) =>
        setEntries((previous) => ({ ...previous, [id]: entry })),
      unregister: (id) =>
        setEntries((previous) => {
          const { [id]: _removed, ...rest } = previous;
          return rest;
        }),
    };
  }, [entries]);

  return (
    <OverlayBotContext.Provider value={value}>{children}</OverlayBotContext.Provider>
  );
}

/** Read the currently registered page context — for the overlay itself. */
export const useOverlayBotContext = (): OverlayBotPageContext => {
  const value = useContext(OverlayBotContext);
  return { context: value?.context, helpText: value?.helpText };
};

/**
 * Register this page's context with the overlay assistant.
 *
 * ```tsx
 * useOverlayBotPage({
 *   helpText: t('overlayBot.help.operatorResponsibility'),
 *   context: 'Der Nutzer sieht die Kontextregeln der Betreiberverantwortung.',
 * });
 * ```
 */
export const useOverlayBotPage = (entry: OverlayBotPageContext) => {
  const value = useContext(OverlayBotContext);
  const id = useMemo(() => nextId(), []);
  const { context, helpText } = entry;

  useEffect(() => {
    if (!value) return undefined;
    value.register(id, { context, helpText });
    return () => value.unregister(id);
    // `value` changes on every registration; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, context, helpText]);
};
